// app/components/AdvancedChart/hooks/useUserScriptIndicator.ts
import { useEffect, useRef } from 'react'
import { UTCTimestamp, ISeriesApi, LineSeries } from 'lightweight-charts'

interface UseUserScriptIndicatorProps {
  userScriptCode: string | null | undefined
  chartData: any[]
  // mainChartRef can be further typed as IChartApi if desired.
  mainChartRef: React.MutableRefObject<any>
}

/**
 * Custom hook that executes the user script code to add a custom indicator
 * to the main chart. It cleans up any previously added user indicator and updates
 * whenever the userScriptCode or chartData changes.
 */
export function useUserScriptIndicator({
  userScriptCode,
  chartData,
  mainChartRef
}: UseUserScriptIndicatorProps) {
  // Ref to hold the user indicator series if any.
  const userIndicatorSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)

  useEffect(() => {
    // Exit if chart is not ready.
    if (!mainChartRef.current) return

    // If no script is provided, remove any previous user indicator.
    if (!userScriptCode) {
      if (userIndicatorSeriesRef.current && mainChartRef.current) {
        try {
          // Additional validation to ensure the series and chart are still valid
          if (mainChartRef.current.removeSeries && typeof mainChartRef.current.removeSeries === 'function') {
            mainChartRef.current.removeSeries(userIndicatorSeriesRef.current)
          }
        } catch (error) {
          console.warn('Failed to remove user indicator series when clearing script:', error)
        } finally {
          userIndicatorSeriesRef.current = null
        }
      }
      return
    }

    // Remove any existing series to keep only one copy.
    if (userIndicatorSeriesRef.current && mainChartRef.current) {
      try {
        // Additional validation to ensure the series and chart are still valid
        if (mainChartRef.current.removeSeries && typeof mainChartRef.current.removeSeries === 'function') {
          mainChartRef.current.removeSeries(userIndicatorSeriesRef.current)
        }
      } catch (error) {
        console.warn('Failed to remove existing user indicator series:', error)
      } finally {
        userIndicatorSeriesRef.current = null
      }
    }

    try {
      // Wrap the script code so that it returns a function named main.
      const wrappedScript = `
        ${userScriptCode}
        return (typeof main === 'function') ? main : null;
      `
      const userFunc = new Function(wrappedScript)()
      if (typeof userFunc !== 'function') {
        console.error('No function main(data) found in user script.')
        return
      }
      // Format the candle data for the function.
      const formattedData = chartData.map(d => ({
        ...d,
        time: d.time as unknown as UTCTimestamp
      }))
      // Execute the user function with the data.
      const resultData = userFunc(formattedData)
      console.log('useUserScriptIndicator: resultData', resultData)

      if (!Array.isArray(resultData) || resultData.length === 0) {
        console.error('User script returned no data.')
        return
      }

      // Validate and filter the result data: ensure each point has a valid time and a numeric value.
      const validData = resultData.filter((pt: any) => {
        const timeValid =
          (typeof pt.time === 'string' || typeof pt.time === 'number') &&
          pt.time !== null &&
          pt.time !== undefined
        const valueValid =
          typeof pt.value === 'number' &&
          !isNaN(pt.value) &&
          isFinite(pt.value)
        return timeValid && valueValid
      })

      if (validData.length === 0) {
        console.error('User script returned no valid data points.')
        return
      }

      // Create a new line series for the user indicator using the new v5 API.
      userIndicatorSeriesRef.current = mainChartRef.current.addSeries(LineSeries, {
        color: 'yellow',
        lineWidth: 2
      })
      // Set the custom indicator data.
      userIndicatorSeriesRef.current?.setData(validData)
      console.log('useUserScriptIndicator: user indicator series created and data set.')
    } catch (err) {
      console.error('Error running user script:', err)
    }
  }, [userScriptCode, chartData, mainChartRef])

  // Cleanup effect to remove series when component unmounts
  useEffect(() => {
    return () => {
      if (userIndicatorSeriesRef.current && mainChartRef.current) {
        try {
          mainChartRef.current.removeSeries(userIndicatorSeriesRef.current)
        } catch (error) {
          console.warn('Failed to remove user indicator series on cleanup:', error)
        }
        userIndicatorSeriesRef.current = null
      }
    }
  }, [mainChartRef])

  return userIndicatorSeriesRef
}
